import React, { ChangeEvent, FC, useState } from 'react';
import {
  Box,
  Button,
  FormControlLabel,
  FormGroup,
  Switch,
  TextField,
  Tooltip,
  useTheme,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { ICustomSurveyQuestion } from '../../../shared/interfaces/ICustomSurveyQuestion';
import Accordion from '../../../components/Accordion/Accordion';
import { AccordionSummaryContentBox } from '../../../components/Survey/Survey';
import { Add, Delete, InfoOutlined, Save } from '@mui/icons-material';
import SurveyService from '../../../services/SurveyService';
import { ISurveysEntry } from '../../../shared/interfaces/ISurveysEntry';
import Chip from '../../../components/Chip/Chip';
import Typography from '@mui/material/Typography';
import { ICustomSurveyQuestionOption } from '../../../shared/interfaces/ICustomSurveyQuestionOption';
import { QuestionTypes } from '../../../shared/interfaces/ISurvey';
import { ICustomSurveyMatrixRow } from '../../../shared/interfaces/ICustomSurveyMatrixRow';
import {
  addMatrixRow,
  canRemoveMatrixColumn,
  canRemoveMatrixRow,
  removeMatrixRow,
} from '../../../shared/helpers/SurveyMatrix';

interface Props {
  question: ICustomSurveyQuestion;
  survey: ISurveysEntry;
  reorderAndReload: (qid: string | undefined) => Promise<void>;
  reload: (qid: string | undefined) => Promise<void>;
}

const SurveyQuestion: FC<Props> = ({ survey, question, reload, reorderAndReload }) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const [current, setCurrent] = useState<ICustomSurveyQuestion>(question);
  const fixedGateOptions: ICustomSurveyQuestionOption[] = [{ label: 'yes' }, { label: 'no' }];

  const deleteQuestion = async () => {
    await reorderAndReload(question.questionId);
  };

  const addOption = async () => {
    const options = current.options || [];
    setCurrent({
      ...current,
      options: options.concat([{ label: 'Neu ' + (options.length + 1) }]),
    });
  };

  const removeOption = async (index: number) => {
    const oldOptions = current.options || [];

    let options = [];
    for (let i = 0; i < oldOptions.length; i++) {
      if (i !== index) {
        options.push(oldOptions[i]);
      }
    }
    setCurrent({
      ...current,
      options: options,
    });
  };

  const handleOptional = (event: React.ChangeEvent<HTMLInputElement>) => {
    setCurrent({
      ...current,
      optional: event.target.checked,
    });
  };

  const handleAllowCustom = (event: React.ChangeEvent<HTMLInputElement>) => {
    setCurrent({
      ...current,
      allowCustom: event.target.checked,
    });
  };

  const getQuestionTypeName = (type: QuestionTypes) => {
    switch (type) {
      case 'CUSTOMSINGLE':
        return t('survey_question_customsingle');
      case 'CUSTOMMULTIPLE':
        return t('survey_question_custommultiple');
      case 'CUSTOMTEXT':
        return t('survey_question_customtext');
      case 'CUSTOMYESNO':
        return t('survey_question_customyesno');
      case 'CUSTOMSCALENPS':
        return t('survey_question_customscalenps');
      case 'CUSTOMDROPDOWN':
        return t('survey_question_customdropdown');
      case 'CUSTOMGATE':
        return t('survey_question_customgate');
      case 'CUSTOMFEEDBACK':
        return t('survey_question_feedback');
      case 'CUSTOMMATRIX':
        return t('survey_question_custommatrix');
      default:
        return type;
    }
  };

  const getGateOptionLabel = (label: string) => {
    if (label === 'yes') {
      return t('yes');
    }

    if (label === 'no') {
      return t('no');
    }

    return label;
  };

  const renderOptions = (type: QuestionTypes) => {
    let options = current.options || [];
    const optionsToRender = type === 'CUSTOMGATE' ? fixedGateOptions : options;

    return (
      <>
        {optionsToRender.map((option: ICustomSurveyQuestionOption, index: number) => {
          return (
            <Box
              key={index + '.' + option.label}
              sx={{
                gap: theme.spacing(1),
                mb: theme.spacing(1),
                display: 'flex',
                alignItems: 'stretch',
              }}
            >
              <TextField
                fullWidth
                size="small"
                defaultValue={
                  type === 'CUSTOMGATE' ? getGateOptionLabel(option.label) : option.label
                }
                multiline={true}
                disabled={type === 'CUSTOMGATE'}
                onChange={
                  type === 'CUSTOMGATE'
                    ? undefined
                    : (event: ChangeEvent<HTMLInputElement>) => (option.label = event.target.value)
                }
              />
              {type !== 'CUSTOMYESNO' && type !== 'CUSTOMGATE' && (
                <Button
                  variant="contained"
                  startIcon={<Delete />}
                  disabled={type === 'CUSTOMMATRIX' && !canRemoveMatrixColumn(options)}
                  onClick={() => removeOption(index)}
                />
              )}
            </Box>
          );
        })}
        {current.allowCustom && (
          <Box
            key={'_'}
            sx={{
              gap: theme.spacing(1),
              mb: theme.spacing(1),
              display: 'flex',
              alignItems: 'stretch',
            }}
          >
            <TextField
              fullWidth
              size="small"
              defaultValue={t('otheroption')}
              multiline={true}
              value={t('otheroption')}
              disabled={true}
            />
            <Button
              variant="contained"
              startIcon={<Delete />}
              onClick={() =>
                setCurrent({
                  ...current,
                  allowCustom: false,
                })
              }
            />
          </Box>
        )}
        {type !== 'CUSTOMYESNO' && type !== 'CUSTOMGATE' && (
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={addOption}
          />
        )}
      </>
    );
  };

  const renderNpsOptions = () => {
    if (!current.options || current.options.length !== 2) {
      setCurrent({
        ...current,
        options: [{ label: 'Sehr unwahrscheinlich' }, { label: 'Sehr wahrscheinlich' }],
      });
      return null;
    }

    return (
      <>
        <Box
          sx={{
            gap: theme.spacing(1),
            mb: theme.spacing(1),
            display: 'flex',
            alignItems: 'stretch',
          }}
        >
          <TextField
            fullWidth
            multiline={true}
            label={t('scale0_label')}
            size="small"
            defaultValue={current.options[0].label}
            onChange={(event: ChangeEvent<HTMLInputElement>) =>
              (current.options[0].label = event.target.value)
            }
          />
        </Box>
        <Box
          sx={{
            gap: theme.spacing(1),
            mb: theme.spacing(1),
            display: 'flex',
            alignItems: 'stretch',
          }}
        >
          <TextField
            fullWidth
            multiline={true}
            label={t('scale10_label')}
            size="small"
            defaultValue={current.options[1].label}
            onChange={(event: ChangeEvent<HTMLInputElement>) =>
              (current.options[1].label = event.target.value)
            }
          />
        </Box>
      </>
    );
  };

  const renderMatrixRows = () => {
    const rows = current.rows || [];
    return (
      <>
        {rows.map((row: ICustomSurveyMatrixRow, index: number) => (
          <Box
            key={row.id}
            sx={{
              gap: theme.spacing(1),
              mb: theme.spacing(1),
              display: 'flex',
              alignItems: 'stretch',
            }}
          >
            <TextField
              fullWidth
              size="small"
              defaultValue={row.label}
              multiline={true}
              onChange={(event: ChangeEvent<HTMLInputElement>) => (row.label = event.target.value)}
            />
            <Button
              variant="contained"
              startIcon={<Delete />}
              disabled={!canRemoveMatrixRow(rows)}
              onClick={() => setCurrent({ ...current, rows: removeMatrixRow(rows, index) })}
            />
          </Box>
        ))}
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => setCurrent({ ...current, rows: addMatrixRow(rows) })}
        />
      </>
    );
  };

  const canSave = () => {
    if (
      current.ShouldShowPrivacyNotice &&
      (!current.PrivacyNoticeLink || current.PrivacyNoticeLink.length === 0)
    ) {
      return false;
    }
    return true;
  };

  const saveQuestion = async () => {
    await SurveyService.updateQuestion(survey.type, survey.id, question.questionId || '', {
      ...current,
      options: current.type === 'CUSTOMGATE' ? fixedGateOptions : current.options,
      position: question.position,
    });
    await reload('');
    setIsExpanded(false);
  };

  const typeName = getQuestionTypeName(question.type);

  return (
    <Accordion
      expanded={isExpanded}
      onExpandRequest={() => setIsExpanded((prevState) => !prevState)}
      summary={
        <AccordionSummaryContentBox>
          <Typography sx={{ whiteSpace: 'pre-wrap' }}>
            {question.position}. {question.question}
          </Typography>
          <Chip
            size="small"
            label={typeName || question.type}
            sx={{ ml: theme.spacing(2) }}
          />
          {question.type === 'CUSTOMGATE' && (
            <Tooltip
              title={t('selection_question_answers_info')}
              arrow
            >
              <InfoOutlined
                fontSize="small"
                sx={{ color: theme.palette.primary.main }}
              />
            </Tooltip>
          )}
        </AccordionSummaryContentBox>
      }
      details={
        <>
          <TextField
            fullWidth
            multiline={true}
            size="small"
            defaultValue={current.question}
            label={t('question_text')}
            sx={{ mb: theme.spacing(2) }}
            onChange={(event: ChangeEvent<HTMLInputElement>) =>
              (current.question = event.target.value)
            }
          />
          {(question.type === 'CUSTOMTEXT' || question.type === 'CUSTOMFEEDBACK') && (
            <>
              <TextField
                fullWidth
                size="small"
                defaultValue={current.placeholder}
                multiline={true}
                label={t('question_placeholder')}
                sx={{ mb: theme.spacing(2) }}
                onChange={(event: ChangeEvent<HTMLInputElement>) =>
                  (current.placeholder = event.target.value)
                }
              />

              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={current.ShouldShowPrivacyNotice}
                    onChange={(event) => {
                      setCurrent({
                        ...current,
                        ShouldShowPrivacyNotice: event.target.checked,
                      });
                    }}
                  />
                }
                label={t('show_privacy_notice')}
                sx={{ mb: theme.spacing(2), ml: theme.spacing(0) }}
              />

              {current.ShouldShowPrivacyNotice && (
                <>
                  <TextField
                    fullWidth
                    size="small"
                    defaultValue={current.PrivacyNoticeText}
                    multiline={true}
                    label={t('survey_privacy_notice')}
                    sx={{ mb: theme.spacing(2) }}
                    onChange={(event: ChangeEvent<HTMLInputElement>) =>
                      setCurrent((prev) => ({ ...prev, PrivacyNoticeText: event.target.value }))
                    }
                  />
                  <TextField
                    fullWidth
                    size="small"
                    required={true}
                    multiline={true}
                    defaultValue={current.PrivacyNoticeLink}
                    value={current.PrivacyNoticeLink}
                    label={t('survey_privacy_notice_link')}
                    sx={{ mb: theme.spacing(2) }}
                    onChange={(event: ChangeEvent<HTMLInputElement>) =>
                      setCurrent((prev) => ({ ...prev, PrivacyNoticeLink: event.target.value }))
                    }
                  />
                </>
              )}
            </>
          )}
          {question.type !== 'CUSTOMGATE' && (
            <FormGroup>
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={current.optional}
                    onChange={handleOptional}
                  />
                }
                label={t('question_optional')}
                sx={{ mb: theme.spacing(2), ml: theme.spacing(0) }}
              />
            </FormGroup>
          )}
          {(question.type === 'CUSTOMSINGLE' || question.type === 'CUSTOMMULTIPLE') && (
            <FormGroup>
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={current.allowCustom}
                    onChange={handleAllowCustom}
                  />
                }
                label={t('question_custom')}
                sx={{ mb: theme.spacing(2), ml: theme.spacing(0) }}
              />
            </FormGroup>
          )}
          {(question.type === 'CUSTOMSINGLE' ||
            question.type === 'CUSTOMDROPDOWN' ||
            question.type === 'CUSTOMYESNO' ||
            question.type === 'CUSTOMMULTIPLE' ||
            question.type === 'CUSTOMGATE') && (
            <>
              <Typography>{t('question_options')}</Typography>
              <Box sx={{ mb: theme.spacing(2) }}>{renderOptions(question.type)}</Box>
            </>
          )}
          {question.type === 'CUSTOMSCALENPS' && (
            <>
              <Box sx={{ mb: theme.spacing(2) }}>{renderNpsOptions()}</Box>
            </>
          )}
          {question.type === 'CUSTOMMATRIX' && (
            <>
              <Typography>{t('question_matrix_rows')}</Typography>
              <Box sx={{ mb: theme.spacing(2) }}>{renderMatrixRows()}</Box>
              <Typography>{t('question_matrix_columns')}</Typography>
              <Box sx={{ mb: theme.spacing(2) }}>{renderOptions(question.type)}</Box>
            </>
          )}

          <Button
            variant="contained"
            disabled={!canSave()}
            startIcon={<Save />}
            sx={{ mr: theme.spacing(1) }}
            onClick={saveQuestion}
          >
            {t('save')}
          </Button>
          <Button
            variant="contained"
            startIcon={<Delete />}
            sx={{ mr: theme.spacing(1) }}
            onClick={deleteQuestion}
          >
            {t('delete_question')}
          </Button>
        </>
      }
    />
  );
};

export default SurveyQuestion;
